import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, MapPin, ArrowUpRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Corporate Video",
  "Nonprofit Fundraising",
  "Podcast Production",
  "Video Course",
  "Other",
];

const CONTACT = {
  name: "Benny Dukelsky",
  email: "Benny@insightmediallc.com",
  phone: "551-206-4494",
  phoneRaw: "5512064494",
  whatsappRaw: "15512064494",
};

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: PROJECT_TYPES[0],
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we'll be in touch within 1 business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        project_type: PROJECT_TYPES[0],
        budget: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-36 px-6 md:px-12 lg:px-16 border-t border-[#1a1a1a]"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left — pitch + contact details */}
        <div className="lg:col-span-5 reveal">
          <p className="eyebrow text-[#E60000] mb-5">/ Contact</p>
          <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9]">
            Let's make<br />
            something<br />
            <span className="text-outline-red">meaningful.</span>
          </h2>

          <p className="mt-8 text-white/70 leading-relaxed md:text-lg max-w-md">
            Tell us a little about your project. We reply to every inquiry
            personally within one business day.
          </p>

          {/* Direct contact rows */}
          <div className="mt-12 border-t border-[#1a1a1a]">
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="contact-email-link"
              className="group flex items-center justify-between py-6 border-b border-[#1a1a1a] hover:text-[#E60000] transition-colors"
            >
              <div className="flex items-center gap-4">
                <Mail size={18} className="text-[#E60000]" />
                <div>
                  <p className="eyebrow text-white/50">Email</p>
                  <p className="font-display-md text-base md:text-lg mt-1">
                    {CONTACT.email}
                  </p>
                </div>
              </div>
              <ArrowUpRight size={20} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            <a
              href={`tel:+1${CONTACT.phoneRaw}`}
              data-testid="contact-phone-link"
              className="group flex items-center justify-between py-6 border-b border-[#1a1a1a] hover:text-[#E60000] transition-colors"
            >
              <div className="flex items-center gap-4">
                <Phone size={18} className="text-[#E60000]" />
                <div>
                  <p className="eyebrow text-white/50">Phone / Text</p>
                  <p className="font-display-md text-base md:text-lg mt-1">
                    {CONTACT.phone}
                  </p>
                </div>
              </div>
              <ArrowUpRight size={20} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            <a
              href={`https://wa.me/${CONTACT.whatsappRaw}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="group flex items-center justify-between py-6 border-b border-[#1a1a1a] hover:text-[#E60000] transition-colors"
            >
              <div className="flex items-center gap-4">
                <MessageCircle size={18} className="text-[#E60000]" />
                <div>
                  <p className="eyebrow text-white/50">WhatsApp</p>
                  <p className="font-display-md text-base md:text-lg mt-1">
                    {CONTACT.phone}
                  </p>
                </div>
              </div>
              <ArrowUpRight size={20} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            <div className="py-6 flex items-center gap-4 text-white/60">
              <MapPin size={18} className="text-[#E60000]" />
              <div>
                <p className="eyebrow text-white/50">Based in</p>
                <p className="font-display-md text-base md:text-lg mt-1 text-white">
                  New Jersey — working worldwide
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="lg:col-span-7 reveal">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-[#0a0a0a] border border-[#1a1a1a] p-6 md:p-12"
          >
            <p className="eyebrow text-white/40 mb-8">/ Project inquiry</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              <div>
                <label className="eyebrow text-white/60 block mb-2">
                  Name *
                </label>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Your full name"
                  className="input-line"
                  required
                />
              </div>

              <div>
                <label className="eyebrow text-white/60 block mb-2">
                  Email *
                </label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="you@organization.org"
                  className="input-line"
                  required
                />
              </div>

              <div>
                <label className="eyebrow text-white/60 block mb-2">
                  Phone
                </label>
                <input
                  data-testid="contact-input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange("phone")}
                  placeholder="(optional)"
                  className="input-line"
                />
              </div>

              <div>
                <label className="eyebrow text-white/60 block mb-2">
                  Project Type
                </label>
                <select
                  data-testid="contact-input-project-type"
                  value={form.project_type}
                  onChange={onChange("project_type")}
                  className="input-line appearance-none cursor-pointer"
                >
                  {PROJECT_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-[#0a0a0a]">
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="eyebrow text-white/60 block mb-2">
                  Estimated Budget
                </label>
                <input
                  data-testid="contact-input-budget"
                  type="text"
                  value={form.budget}
                  onChange={onChange("budget")}
                  placeholder="e.g. $10k – $25k  (optional)"
                  className="input-line"
                />
              </div>

              <div className="md:col-span-2">
                <label className="eyebrow text-white/60 block mb-2">
                  Tell us about your project *
                </label>
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Goals, audience, timeline, anything we should know…"
                  rows={5}
                  className="input-line"
                  required
                />
              </div>
            </div>

            <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <p className="text-xs text-white/40 max-w-sm">
                By submitting, you agree to be contacted by Insight Media
                Productions about your project. We never share your info.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Send Inquiry →"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
